import { BrandKit } from "@redotech/redo-model/brand-kit";
import { MarketingForgeElementType } from "@redotech/redo-model/content-builder/element-schema";
import { SignupForm } from "@redotech/redo-model/signup-form-schema";
import { memo, NamedExoticComponent, useRef, useState } from "react";
import { Flex } from "../flex";
import { SignupFormDiscountCodeDisplay } from "./form-elements/discount-code";
import { FormButton } from "./form-elements/form-button";
import { FormColumn } from "./form-elements/form-column";
import { FormImage } from "./form-elements/form-image";
import { FormProduct } from "./form-elements/form-product";
import { FormElementProps } from "./form-elements/index";
import { Input } from "./form-elements/input";
import { RichText } from "./form-elements/rich-text";
import { TextToSubscribeButton } from "./form-elements/text-to-subscribe-button";
import { TextToSubscribeDescription } from "./form-elements/text-to-subscribe-description";

const elementComponents: Record<
  Exclude<MarketingForgeElementType, "container">,
  NamedExoticComponent<FormElementProps<any>>
> = {
  [MarketingForgeElementType.INPUT]: Input,
  [MarketingForgeElementType.RICH_TEXT]: RichText,
  [MarketingForgeElementType.BUTTON]: FormButton,
  [MarketingForgeElementType.IMAGE]: FormImage,
  [MarketingForgeElementType.PRODUCT]: FormProduct,
  [MarketingForgeElementType.COLUMN]: FormColumn,
  [MarketingForgeElementType.DISCOUNT]: SignupFormDiscountCodeDisplay,
  [MarketingForgeElementType.TEXT_TO_SUBSCRIBE_BUTTON]: TextToSubscribeButton,
  [MarketingForgeElementType.TEXT_TO_SUBSCRIBE_DESCRIPTION]:
    TextToSubscribeDescription,
};

export interface SignupFormProps {
  form: SignupForm;
  currentStep: number;
  brandKit: BrandKit;
  discountCode?: string;
  merchantPhoneNumber?: string;
  anonymousId?: string;
  onClose?: () => void;
  onSubmitStep: (_: {
    stepIndex: number;
    formData: Record<string, string>;
    shouldAdvance?: boolean;
  }) => Promise<void>;
}

export const BaseSignupForm = memo(function BaseSignupForm({
  form,
  brandKit,
  currentStep: currentStepIndex,
  discountCode,
  merchantPhoneNumber,
  anonymousId,
  onClose,
  onSubmitStep,
}: SignupFormProps) {
  const [submitting, setSubmitting] = useState(false);
  const pendingRedirectUrl = useRef<string | undefined>(undefined);
  const currentStep = form.steps[currentStepIndex];
  if (!currentStep) {
    return null;
  }
  return (
    <div>
      <form
        onSubmit={async (e) => {
          setSubmitting(true);
          e.preventDefault();
          const formData = new FormData(e.currentTarget);
          const transformedData: Record<string, string> = {};
          for (const [key, value] of formData.entries()) {
            transformedData[key] = value as string;
          }
          try {
            await onSubmitStep({
              stepIndex: currentStepIndex,
              formData: transformedData,
              shouldAdvance: !pendingRedirectUrl.current,
            });
            setSubmitting(false);
            if (pendingRedirectUrl.current) {
              window.location.href = pendingRedirectUrl.current;
            }
          } catch (e) {
            console.error(e);
            setSubmitting(false);
          } finally {
            pendingRedirectUrl.current = undefined;
          }
        }}
      >
        {/* key resets the input element's state when the currentStep changes */}
        <Flex dir="column" gap="xl" key={currentStepIndex} py="xl">
          {currentStep.elements.map((element, index) => {
            const ElementComponent = elementComponents[element.type];
            return (
              <div key={index}>
                <ElementComponent
                  anonymousId={anonymousId}
                  backgroundColor={form.layout.backgroundColor}
                  brandKit={brandKit}
                  brandKitOverrides={form.theme.overrides}
                  discountContext={
                    discountCode ? { type: "active", discountCode } : undefined
                  }
                  element={element}
                  layoutType={form.layout.type}
                  merchantPhoneNumber={merchantPhoneNumber}
                  onClose={onClose}
                  onWillSubmit={(url) => {
                    pendingRedirectUrl.current = url;
                  }}
                  submitting={submitting}
                />
              </div>
            );
          })}
        </Flex>
      </form>
    </div>
  );
});
